/**
 * @author NikolaV
 */

import { Ajax, Ajax2 } from '@shared/Ajax';
import { CanvasUtils } from '@shared/utils/CanvasUtils';

// svgs have separate BW versions, but for pngs we apply grayscale filter
import iconAsphalt from '../../../assets/images/vehicle_icons/asphalt-paving-machine-active.svg';
import iconAsphaltBW from '../../../assets/images/vehicle_icons/asphalt-paving-machine-inactive.svg';
import iconBackhoe from '../../../assets/images/vehicle_icons/backhoe-active.svg';
import iconBackhoeBW from '../../../assets/images/vehicle_icons/backhoe-inactive.svg';
import iconBarge from '../../../assets/images/vehicle_icons/barge-active.svg';
import iconBargeBW from '../../../assets/images/vehicle_icons/barge-inactive.svg';
import iconBasestation from '../../../assets/images/vehicle_icons/base-station-active.svg';
import iconBasestationBW from '../../../assets/images/vehicle_icons/base-station-inactive.svg';
import iconBulldozer from '../../../assets/images/vehicle_icons/bulldozer-active.svg';
import iconBulldozerBW from '../../../assets/images/vehicle_icons/bulldozer-inactive.svg';
import iconDumper from '../../../assets/images/vehicle_icons/dumper-active.svg';
import iconDumperBW from '../../../assets/images/vehicle_icons/dumper-inactive.svg';
import iconDriller from '../../../assets/images/vehicle_icons/driller-active.svg';
import iconDrillerBW from '../../../assets/images/vehicle_icons/driller-inactive.svg';
import iconExcavator from '../../../assets/images/vehicle_icons/excavator-active.svg';
import iconExcavatorBW from '../../../assets/images/vehicle_icons/excavator-inactive.svg';
import iconGrader from '../../../assets/images/vehicle_icons/grader-active.svg';
import iconGraderBW from '../../../assets/images/vehicle_icons/grader-inactive.svg';
import iconLoader from '../../../assets/images/vehicle_icons/loader-active.svg';
import iconLoaderBW from '../../../assets/images/vehicle_icons/loader-inactive.svg';
import iconMilling from '../../../assets/images/vehicle_icons/milling-active.svg';
import iconMillingBW from '../../../assets/images/vehicle_icons/milling-inactive.svg';
import iconPiling from '../../../assets/images/vehicle_icons/piling-rig-active.svg';
import iconPilingBW from '../../../assets/images/vehicle_icons/piling-rig-inactive.svg';
import iconRoller from '../../../assets/images/vehicle_icons/roller-active.svg';
import iconRollerBW from '../../../assets/images/vehicle_icons/roller-inactive.svg';
import iconRover from '../../../assets/images/vehicle_icons/rover-active.svg';
import iconRoverBW from '../../../assets/images/vehicle_icons/rover-inactive.svg';
import iconSimulator from '../../../assets/images/vehicle_icons/simulator-active.svg';
import iconSimulatorBW from '../../../assets/images/vehicle_icons/simulator-inactive.svg';
import iconTandemRoller from '../../../assets/images/vehicle_icons/tandem-roller-active.svg';
import iconTandemRollerBW from '../../../assets/images/vehicle_icons/tandem-roller-inactive.svg';
import iconTotalstation from '../../../assets/images/vehicle_icons/total-station-active.svg';
import iconTotalstationBW from '../../../assets/images/vehicle_icons/total-station-inactive.svg';
import iconTractor from '../../../assets/images/vehicle_icons/tractor-active.svg';
import iconTractorBW from '../../../assets/images/vehicle_icons/tractor-inactive.svg';
import iconTruck from '../../../assets/images/vehicle_icons/truck-active.svg';
import iconTruckBW from '../../../assets/images/vehicle_icons/truck-inactive.svg';
import iconTunnelrig from '../../../assets/images/vehicle_icons/tunnelrig-active.svg';
import iconTunnelrigBW from '../../../assets/images/vehicle_icons/tunnelrig-active.svg';
import iconUnknown from '../../../assets/images/vehicle_icons/unknown-active.svg';
import iconUnknownBW from '../../../assets/images/vehicle_icons/unknown-inactive.svg';

//@ts-ignore
const removeDiacritics = require('diacritics').remove;

export type VehicleStatusColor = 'yellow' | 'red' | 'green' | 'grey';

export interface IVehiclesGrouped {
    active: IVehicle[];
    noPosition: IVehicle[];
    onHold: IVehicle[];
    otherProject: IVehicle[];
    totalCount: number;
}

const TYPES: {
    key: string;
    text: string;
    icon: string;
    iconGrayscale?: string;
}[] = [
    {
        key: 'UNKNOWN',
        text: 'Unknown',
        icon: iconUnknown,
        iconGrayscale: iconUnknownBW,
    },
    {
        key: 'EXCAVATOR',
        text: kuura.getMessage('js_vehicletype.excavator'),
        icon: iconExcavator,
        iconGrayscale: iconExcavatorBW,
    },
    {
        key: 'LOADER',
        text: kuura.getMessage('js_vehicletype.loader'),
        icon: iconLoader,
        iconGrayscale: iconLoaderBW,
    },
    {
        key: 'BULLDOZER',
        text: kuura.getMessage('js_vehicletype.bulldozer'),
        icon: iconBulldozer,
        iconGrayscale: iconBulldozerBW,
    },
    {
        key: 'BACKHOE',
        text: kuura.getMessage('js_vehicletype.backhoe'),
        icon: iconBackhoe,
        iconGrayscale: iconBackhoeBW,
    },
    {
        key: 'PILING',
        text: kuura.getMessage('js_vehicletype.piling'),
        icon: iconPiling,
        iconGrayscale: iconPilingBW,
    },
    {
        key: 'DUMPER',
        text: kuura.getMessage('js_vehicletype.dumper'),
        icon: iconDumper,
        iconGrayscale: iconDumperBW,
    },
    {
        key: 'GRADER',
        text: kuura.getMessage('js_vehicletype.grader'),
        icon: iconGrader,
        iconGrayscale: iconGraderBW,
    },
    {
        key: 'SIMULATOR',
        text: kuura.getMessage('js_vehicletype.simulator'),
        icon: iconSimulator,
        iconGrayscale: iconSimulatorBW,
    },
    {
        key: 'TRUCK',
        text: kuura.getMessage('js_vehicletype.truck'),
        icon: iconTruck,
        iconGrayscale: iconTruckBW,
    },
    {
        key: 'ROVER',
        text: kuura.getMessage('js_vehicletype.rover'),
        icon: iconRover,
        iconGrayscale: iconRoverBW,
    },
    {
        key: 'DRILLER',
        text: kuura.getMessage('js_vehicletype.driller'),
        icon: iconDriller,
        iconGrayscale: iconDrillerBW,
    },
    {
        key: 'BASESTATION',
        text: kuura.getMessage('js_vehicletype.basestation'),
        icon: iconBasestation,
        iconGrayscale: iconBasestationBW,
    },
    {
        key: 'ROLLER',
        text: kuura.getMessage('js_vehicletype.roller'),
        icon: iconRoller,
        iconGrayscale: iconRollerBW,
    },
    {
        key: 'ASPHALT',
        text: kuura.getMessage('js_vehicletype.asphalt'),
        icon: iconAsphalt,
        iconGrayscale: iconAsphaltBW,
    },
    {
        key: 'TUNNELRIG',
        text: kuura.getMessage('js_vehicletype.tunnelrig'),
        icon: iconTunnelrig,
        iconGrayscale: iconTunnelrigBW,
    },
    {
        key: 'BARGE',
        text: kuura.getMessage('js_vehicletype.barge'),
        icon: iconBarge,
        iconGrayscale: iconBargeBW,
    },
    {
        key: 'MILLING',
        text: kuura.getMessage('js_vehicletype.milling'),
        icon: iconMilling,
        iconGrayscale: iconMillingBW,
    },
    {
        key: 'TANDEM_ROLLER',
        text: kuura.getMessage('js_vehicletype.tandem_roller'),
        icon: iconTandemRoller,
        iconGrayscale: iconTandemRollerBW,
    },
    {
        key: 'TOTALSTATION',
        text: kuura.getMessage('js_vehicletype.totalstation'),
        icon: iconTotalstation,
        iconGrayscale: iconTotalstationBW,
    },
    {
        key: 'TRACTOR',
        text: kuura.getMessage('js_vehicletype.tractor'),
        icon: iconTractor,
        iconGrayscale: iconTractorBW,
    },
];

// we can't use svg for icons in openlayers, big performance hit in chrome only, so we have to use pngs
const convertIcons = async () => {
    for (const v of TYPES) {
        v.icon = await CanvasUtils.svgToPng(v.icon, 2);
        v.iconGrayscale = await CanvasUtils.svgToPng(v.iconGrayscale, 2);
    }
};
convertIcons();

const VENDORS = [
    { key: 'UNKNOWN', text: 'Unknown' },
    { key: 'NOVATRON', text: 'Novatron' },
    { key: 'PROLEC', text: 'Prolec' },
    { key: 'DIGPILOT', text: 'DigPilot' },
    { key: 'INFRAKIT', text: 'Infrakit' },
    { key: 'LEICA', text: 'Leica' },
    { key: 'TRIMBLE', text: 'Trimble' },
    { key: 'TOPCON', text: 'Topcon' },
    { key: 'SANDVIK', text: 'Sandvik' },
    { key: 'TECHBELAR', text: 'Techbelar' },
    { key: 'TRIMBLE_EARTHWORKS', text: 'Trimble Earthworks' },
    { key: 'CARLSON', text: 'Carlson' },
    { key: 'GEOMAX', text: 'Geomax' },
    { key: 'MAKIN3D', text: 'Makin 3D' },
    { key: 'ARPENGO', text: 'Arpengo' },
    { key: 'UNICONTROL', text: 'Unicontrol' },
    { key: 'TRIMBLE_GROUNDWORKS', text: 'Trimble Groundworks' },
    { key: 'MARXACT', text: 'marXact' },
    { key: 'DITIO', text: 'Ditio' },
    { key: 'MTS', text: 'Mts' },
    { key: 'FIELDSIGHT', text: 'Fieldsight' },
    { key: 'ROCKMA', text: 'Rockma' },
];

const TypesIndexedByKey = TYPES.reduce((obj, val, i) => {
    obj[val.key] = val;
    return obj;
}, {});

const VendorsIndexedByKey = VENDORS.reduce((obj, val, i) => {
    obj[val.key] = val;
    return obj;
}, {});

const VendorNames = VENDORS.map(v => v.text);

const VendorsIndexedOptions: { [key: number]: { title: string; value: string; active: boolean } } = VendorNames.reduce((obj, val, i) => {
    obj[i] = {
        title: val,
        value: i,
        active: true,
    };
    return obj;
}, {});

const TypeNames = TYPES.map(v => v.text);
const TypesIndexedOptions: { [key: number]: { title: string; value: string; active: boolean } } = TypeNames.reduce((obj, val, i) => {
    obj[i] = {
        title: val,
        value: i,
        active: true,
    };
    return obj;
}, {});

const powerSources = {
    0: kuura.getMessage('js_vehicles.power_source.unknown'),
    1: kuura.getMessage('js_vehicles.power_source.diesel'),
    2: kuura.getMessage('js_vehicles.power_source.petrol'),
    3: kuura.getMessage('js_vehicles.power_source.electric'),
    4: kuura.getMessage('js_vehicles.power_source.biodiesel'),
};

const emissionClasses = {
    0: kuura.getMessage('js_vehicles.power_source.unknown'),
    1: 'EURO 1',
    2: 'EURO 2',
    3: 'EURO 3',
    4: 'EURO 4',
    5: 'EURO 5',
    6: 'EURO 6',
    7: 'EURO 7',
    8: 'STAGE I',
    9: 'STAGE II',
    10: 'STAGE IIIA',
    11: 'STAGE IIIB',
    12: 'STAGE IV',
    13: 'STAGE V',
};

// sets additional fields on object that are needed later
function preProcessEntity(ajaxData: IVehicle): IVehicle {
    const v = Object.assign({}, ajaxData);

    v.vendorString = VendorNames[v.vendor]; // vendor int to string
    v.typeString = TypeNames[v.type]; // type int to string

    const warnings = getWarnings(v);

    v.calibWarningsMsgs = warnings.join('\x0A');
    v.calibWarningSigns = warnings.length > 1 ? '!!' : '!';

    v.nameLatin = removeDiacritics(v.name);
    return v;
}

function groupByStatus(vehicles: IVehicle[]): IVehiclesGrouped {
    const empty: IVehiclesGrouped = {
        active: [],
        noPosition: [],
        onHold: [],
        otherProject: [],
        totalCount: 0,
    };

    const grouped = _.groupBy(vehicles, v => {
        if (v.onHold) return 'onHold';
        if (v.activeProject && v.activeProject.id != kuura.getProjectId()) return 'otherProject';
        if (v.position && typeof v.position.latitude === 'number' && typeof v.position.longitude === 'number') return 'active';
        return 'noPosition';
    });

    return {
        ...empty,
        ...grouped,
        totalCount: vehicles.length,
    };
}

function getWarnings(vehicle: IVehicle, tolerance: number = 0.05) {
    const messages = [];

    // Calibration x/y/z difference [{0}/{1}/{2}] cm (vehicles.calibration_over)
    const diffs = vehicle.calibrationDifference;
    const diffWarning = diffs[0] != null && (diffs[0] > tolerance || diffs[1] > tolerance || diffs[2] > tolerance);
    if (diffWarning) {
        messages.push(kuura.getMessage('js_vehicles.calibration_difference', diffs[0], diffs[1], diffs[2]));
    }

    // Last calibration over 2 weeks ago ({0}) (vehicles.calibration_difference)
    if (vehicle.calibrationWarning) {
        messages.push(kuura.getMessage('js_vehicles.calibration_over', '-'));
    }

    return messages;
}

function getCalibrationStatus(vehicle: IVehicle): IVehicleCalibrationStatus {
    let tol = vehicle.accuracyTolerance;

    if (!tol || tol.enabled) {
        tol = vehicle.activeProject.accuracyTolerance ||
            kuura.getAccuracyTolerance() || {
                x: 0.05,
                y: 0.05,
                z: 0.05,
                enabled: true,
                source: 'PROJECT',
            };

        if (tol) {
            tol.source = 'PROJECT';
        }
    }

    if (!tol) {
        console.warn('No accuracy tolerance data found for vehicle: ', vehicle);
        return null;
    }

    const diffs = vehicle.calibrationDifference;
    if (diffs && diffs[0] != null) {
        const acc: IVehicleCalibrationStatus = {
            xValid: diffs[0] < tol.x,
            yValid: diffs[1] < tol.y,
            zValid: diffs[2] < tol.z,
            allDeltasValid: false,
            tol,
        };
        acc.allDeltasValid = acc.xValid && acc.yValid && acc.zValid;
        return acc;
    } else {
        return null;
    }
}

// returns all vehicles
export function fetchForActiveProject(): JQueryPromise<IVehicle[]> {
    return Ajax({
        url: 'api/1/project/' + kuura.getProjectId() + '/vehicles.json',
    }).then((resp: { vehicleList: IVehicle[] }) => {
        return resp.vehicleList.map(preProcessEntity);
    });
}

// returns vehicles with position
export function fetchWithPositions(projectId?: number): JQueryPromise<IVehicle[]> {
    let data = {};
    if ($.isNumeric(projectId)) {
        data = {
            projectId,
        };
    }
    return Ajax({
        url: 'api/ajax_vehicles.json',
        data,
    }).then((resp: { vehicles: IVehicle[] }) => {
        return resp.vehicles.map(preProcessEntity);
    });
}

function fetchAccuracyCounts(): JQueryPromise<{ [key: string]: number }> {
    return Ajax({
        url: 'vehicles/accuracy_counts.json',
    }).then(resp => resp.counts);
}

export function sendMessage(vehicleId: number, message: string): JQueryPromise<any> {
    return Ajax({
        url: 'ajax_new_vehicle_message.json',
        method: 'POST',
        data: {
            vehicleId,
            message,
        },
    });
}

export function deleteAccuracyLogpoints(vehicleId: number, accuracyLogpointId: number): JQueryPromise<any> {
    return Ajax({
        url: `vehicles/${vehicleId}/accuracy/${accuracyLogpointId}/delete`,
        method: 'post',
    });
}

export function getEfficiencyForWeek(week: Date): JQueryPromise<any> {
    return Ajax({
        url: 'ajax_vehicles_efficiency_for_weeks.json',
        data: {
            week,
        },
    });
}

export function getActivityCalendar(vehicleId: number, start: number, end: number): JQueryPromise<any> {
    return Ajax({
        url: 'ajax_calendar_events.json',
        data: {
            vehicleId,
            start,
            end,
        },
    });
}

export function getCalendarErrors(vehicleId: number, start: number, end: number): JQueryPromise<any> {
    return Ajax({
        url: 'ajax_calendar_error_number.json',
        data: {
            vehicleId,
            start,
            end,
        },
    });
}

export function getCalendarActiveModelEvents(vehicleId: number, start: number, end: number): JQueryPromise<any> {
    return Ajax({
        url: 'ajax_calendar_active_model_events.json',
        data: {
            vehicleId,
            start,
            end,
        },
    });
}

export function getCalendarActiveModelList(vehicleId: number, start: number, end: number): JQueryPromise<any> {
    return Ajax({
        url: 'ajax_active_models_list.json',
        data: {
            vehicleId,
            start,
            end,
        },
    });
}

export function getModelRights(vehicleId: number): JQueryPromise<any> {
    return Ajax({
        url: `vehicles/${vehicleId}/models`,
    });
}

export function setModelRights(vehicleId: number, modelIds: number[], linkIds: number[]): JQueryPromise<any> {
    const projectId: number = kuura.getProjectId();
    return Ajax({
        url: `vehicles/${vehicleId}/models`,
        method: 'post',
        data: {
            modelIds,
            linkIds,
            projectId,
        },
    });
}

export function copyModelRights(fromVehicleId: number, toVehicleIds: number[]): JQueryPromise<any> {
    const projectId: number = kuura.getProjectId();
    return Ajax({
        url: `vehicles/${fromVehicleId}/models/copy`,
        method: 'post',
        data: {
            fromVehicleId,
            toVehicleIds,
            projectId,
        },
    });
}

export function fetchEmissions(vehicleUuid: string): Promise<IVehicleEmission> {
    return Ajax2({
        url: `${kuura.getContextPath()}/api/1/vehicle/${vehicleUuid}/vehicle-emissions`,
    });
}

export function createOrUpdateEmissions(vehicleUuid: string, emission: Partial<IVehicleEmission>): Promise<IVehicleEmission> {
    return Ajax2({
        url: `${kuura.getContextPath()}/api/1/vehicle/${vehicleUuid}/vehicle-emissions`,
        method: 'post',
        contentType: 'json',
        data: emission,
    });
}

export const VehiclesService = {
    groupByStatus,
    getWarnings,
    fetchAccuracyCounts,
    fetchForActiveProject,
    fetchWithPositions,
    getCalibrationStatus,
    VENDORS,
    VendorNames,
    VendorsIndexedByKey,
    VendorsIndexedOptions,
    TYPES,
    TypeNames,
    TypesIndexedByKey,
    TypesIndexedOptions,
    sendMessage,
    getCalendarActiveModelEvents,
    getCalendarActiveModelList,
    getCalendarErrors,
    getActivityCalendar,
    getEfficiencyForWeek,
    deleteAccuracyLogpoints,
    fetchEmissions,
    createOrUpdateEmissions,
    powerSources,
    emissionClasses,
};
